import React, { useState, useEffect } from 'react';
import { 
  Navigation, 
  MapPin,
  Users,
  Clock,
  Car,
  Battery,
  Signal,
  Phone,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  RefreshCw,
  Filter,
  Map,
  Truck
} from 'lucide-react';
import Card from '../../components/Card';

const InstallationTracking = () => {
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Simulate real-time updates
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
      }, 5000); // Update every 5 seconds
      return () => clearInterval(interval);
    }
  }, [isLive]);

  // Date GPS tracking pentru echipe
  const teamLocations = [
    {
      id: 'TEAM-001',
      name: 'Echipa Ștefan',
      leader: 'Ștefan Georgescu',
      status: 'moving',
      location: {
        address: 'Bd. Magheru 12, București',
        lat: 44.4378,
        lng: 26.0986,
        accuracy: 5
      },
      destination: {
        address: 'Str. Florilor 25, Sector 2',
        estimatedArrival: '10:30',
        distance: '2.3 km'
      },
      vehicle: {
        type: 'Duba Ford Transit',
        plate: 'B-123-TBC',
        fuel: 78,
        battery: 95
      },
      currentProject: {
        id: 'INST-2024-045',
        client: 'Familia Popescu',
        type: 'Ferestre PVC',
        priority: 'normal'
      },
      lastUpdate: new Date(Date.now() - 30000), // 30 seconds ago
      signal: 'strong',
      speed: 35,
      direction: 'NE'
    },
    {
      id: 'TEAM-002',
      name: 'Echipa Eusebiu',
      leader: 'Eusebiu Marinescu',
      status: 'on_site',
      location: {
        address: 'Calea Victoriei 78, București',
        lat: 44.4341,
        lng: 26.0963,
        accuracy: 3
      },
      destination: null,
      vehicle: {
        type: 'Dacia Dokker',
        plate: 'B-456-TBC',
        fuel: 45,
        battery: 88
      },
      currentProject: {
        id: 'INST-2024-047',
        client: 'Hotel Continental',
        type: 'Uși de Intrare',
        priority: 'high'
      },
      lastUpdate: new Date(Date.now() - 120000), // 2 minutes ago
      signal: 'medium',
      speed: 0,
      direction: null,
      workStartTime: '08:30',
      estimatedCompletion: '16:00'
    },
    {
      id: 'TEAM-003',
      name: 'Echipa Support',
      leader: 'Disponibil',
      status: 'base',
      location: {
        address: 'Sediu TIBECO - Str. Industriei 15',
        lat: 44.4457,
        lng: 26.0887,
        accuracy: 2
      },
      destination: null,
      vehicle: {
        type: 'Renault Master',
        plate: 'B-789-TBC',
        fuel: 92,
        battery: 100
      },
      currentProject: null,
      lastUpdate: new Date(Date.now() - 600000), // 10 minutes ago
      signal: 'strong',
      speed: 0,
      direction: null
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'moving': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'on_site': return 'bg-green-100 text-green-800 border-green-200';
      case 'base': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'offline': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'moving': return 'În Deplasare';
      case 'on_site': return 'La Client';
      case 'base': return 'La Sediu';
      case 'offline': return 'Offline';
      default: return 'Necunoscut';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'moving': return Truck;
      case 'on_site': return CheckCircle;
      case 'base': return MapPin;
      case 'offline': return AlertTriangle;
      default: return MapPin;
    }
  };

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'strong': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'weak': return 'text-red-600';
      default: return 'text-gray-400';
    }
  };

  const getFuelColor = (fuel) => {
    if (fuel > 50) return 'text-green-600';
    if (fuel > 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return `${seconds}s în urmă`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m în urmă`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h în urmă`;
  };

  const filteredTeams = selectedTeam === 'all' 
    ? teamLocations 
    : teamLocations.filter(team => team.id === selectedTeam);

  // Statistics
  const totalTeams = teamLocations.length;
  const activeTeams = teamLocations.filter(t => t.status !== 'offline').length;
  const onSiteTeams = teamLocations.filter(t => t.status === 'on_site').length;
  const movingTeams = teamLocations.filter(t => t.status === 'moving').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Navigation className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Urmărire GPS Echipe
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Monitorizare în timp real • Ultima actualizare: {lastUpdate.toLocaleTimeString('ro-RO')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Toate Echipele</option>
            {teamLocations.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => setIsLive(!isLive)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isLive 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            {isLive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isLive ? 'Live' : 'Pauză'}
          </button>
          
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <RefreshCw className="w-4 h-4" />
            Actualizează
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Echipe Totale
              </div>
            </div>
            <Users className="w-8 h-8 text-gray-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {activeTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Online
              </div>
            </div>
            <Signal className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {movingTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                În Deplasare
              </div>
            </div>
            <Map className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-primary-600">
                {onSiteTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                La Clienți
              </div>
            </div>
            <CheckCircle className="w-8 h-8 text-primary-600" />
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <Card title="Hartă Locații Live" icon={MapPin}>
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg h-96 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
              Hartă GPS Interactivă
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Aici va fi integrată harta Google Maps cu locațiile echipelor în timp real
            </p>
            <div className="flex justify-center gap-4">
              <div className="flex items-center text-sm">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
                <span>În Deplasare</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="w-3 h-3 bg-green-600 rounded-full mr-2"></div>
                <span>La Client</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="w-3 h-3 bg-gray-600 rounded-full mr-2"></div>
                <span>La Sediu</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Teams Tracking List */}
      <div className="space-y-4">
        {filteredTeams.map((team) => {
          const StatusIcon = getStatusIcon(team.status);
          
          return (
            <Card key={team.id} className="overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-primary-400 rounded-full flex items-center justify-center">
                      <StatusIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {team.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {team.leader}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(team.status)}`}>
                      {getStatusText(team.status)}
                    </span>
                    {isLive && (
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Location Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      Locație Curentă
                    </h4>
                    <div className="text-sm space-y-1">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {team.location.address}
                      </p>
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <span>Precizie: ±{team.location.accuracy}m</span>
                      </div>
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>{getTimeAgo(team.lastUpdate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                      <Car className="w-4 h-4 text-gray-400 mr-2" />
                      Vehicul
                    </h4>
                    <div className="text-sm space-y-1">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {team.vehicle.type}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {team.vehicle.plate}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center">
                          <span className={`text-xs ${getFuelColor(team.vehicle.fuel)}`}>
                            ⛽ {team.vehicle.fuel}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Battery className="w-3 h-3 text-green-600 mr-1" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {team.vehicle.battery}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Movement Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                      <Map className="w-4 h-4 text-gray-400 mr-2" />
                      Deplasare
                    </h4>
                    <div className="text-sm space-y-1">
                      {team.status === 'moving' && team.destination ? (
                        <>
                          <p className="text-gray-900 dark:text-white font-medium">
                            → {team.destination.address}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400">
                            ETA: {team.destination.estimatedArrival} ({team.destination.distance})
                          </p>
                          <p className="text-gray-600 dark:text-gray-400">
                            Viteză: {team.speed} km/h {team.direction}
                          </p>
                        </>
                      ) : team.status === 'on_site' ? (
                        <>
                          <p className="text-green-600 font-medium">La destinație</p>
                          {team.workStartTime && (
                            <p className="text-gray-600 dark:text-gray-400">
                              Începere: {team.workStartTime}
                            </p>
                          )}
                          {team.estimatedCompletion && (
                            <p className="text-gray-600 dark:text-gray-400">
                              Finalizare est.: {team.estimatedCompletion}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">Staționar</p>
                      )}
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                      <Users className="w-4 h-4 text-gray-400 mr-2" />
                      Proiect Activ
                    </h4>
                    <div className="text-sm space-y-1">
                      {team.currentProject ? (
                        <>
                          <p className="text-gray-900 dark:text-white font-medium">
                            {team.currentProject.client}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400">
                            {team.currentProject.type}
                          </p>
                          <span className={`inline-block px-2 py-1 rounded text-xs ${
                            team.currentProject.priority === 'high' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {team.currentProject.priority === 'high' ? 'Prioritate Înaltă' : 'Normal'}
                          </span>
                        </>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">
                          Fără proiect activ
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Bar */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center">
                      <Signal className={`w-4 h-4 mr-1 ${getSignalColor(team.signal)}`} />
                      <span className="text-gray-600 dark:text-gray-400">
                        {team.signal === 'strong' ? 'Semnal Fort' : 
                         team.signal === 'medium' ? 'Semnal Mediu' : 'Semnal Slab'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-gray-600 dark:text-gray-400">
                        Disponibil telefonic
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                      Contactează
                    </button>
                    <button className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors">
                      Istoric
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default InstallationTracking;